import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
    "Oil & Filter Change",
    "Brake Service",
    "Tune-Up",
    "Pre-Purchase Inspection",
    "Diagnostics / Check Engine",
    "Battery & Electrical",
    "Other / Not sure",
];

const TIME_OPTIONS = [
    "06:00 - 08:00",
    "08:00 - 10:00",
    "10:00 - 12:00",
    "12:00 - 14:00",
    "14:00 - 16:00",
    "16:00 - 18:00",
    "18:00 - 20:00",
];

const initial = {
    full_name: "",
    phone: "",
    email: "",
    vehicle_year: "",
    vehicle_make: "",
    vehicle_model: "",
    service_type: "",
    preferred_date: "",
    preferred_time: "",
    address: "",
    issue: "",
};

function Label({ children, htmlFor, idx }) {
    return (
        <label
            htmlFor={htmlFor}
            className="font-mono-tech text-[10px] text-[#52525b] block mb-2"
        >
            <span className="text-[#0a0a0a] mr-2">{idx}</span>
            {children}
        </label>
    );
}

const inputCls =
    "w-full bg-white border border-[#0a0a0a]/15 px-4 py-3.5 text-[15px] font-medium focus:outline-none focus:border-[#0a0a0a] focus:ring-0 placeholder:text-[#a1a1aa] transition-colors";

export default function BookingForm() {
    const [form, setForm] = useState(initial);
    const [dateOpen, setDateOpen] = useState(false);
    const [date, setDate] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        // basic client checks
        const required = [
            "full_name",
            "phone",
            "email",
            "vehicle_year",
            "vehicle_make",
            "vehicle_model",
            "service_type",
            "preferred_date",
            "preferred_time",
            "address",
        ];
        for (const k of required) {
            if (!form[k]) {
                toast.error("Please complete all required fields");
                return;
            }
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/bookings`, form);
            toast.success("Booking received — we'll text you to confirm.");
            setForm(initial);
            setDate(null);
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                "Could not submit booking. Please try again.";
            toast.error(typeof detail === "string" ? detail : "Submission failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="book"
            data-testid="booking-section"
            className="border-b hairline"
        >
            <div className="mx-auto max-w-[1400px] grid grid-cols-12">
                <div className="col-span-12 lg:col-span-4 px-6 md:px-12 py-14 md:py-20 border-r hairline">
                    <div className="font-mono-tech text-[11px] text-[#1d4ed8] mb-6">
                        REQUEST SERVICE
                    </div>
                    <h2 className="font-display text-4xl md:text-5xl tracking-tighter leading-[0.95]">
                        Request <br />
                        <span className="text-[#1d4ed8]">a service.</span>
                    </h2>
                    <p className="text-[#52525b] mt-6 leading-relaxed">
                        Fill the form and we&apos;ll text back within 15 minutes
                        with an exact ETA and quote. No payment required to
                        book.
                    </p>
                    <div className="mt-10 border-t border-l hairline">
                        {[
                            ["RESPONSE", "≤ 15 min"],
                            ["DEPOSIT", "$0"],
                            ["CANCELLATION", "Free until 2h before"],
                        ].map(([k, v]) => (
                            <div
                                key={k}
                                className="border-r border-b hairline p-4 flex items-center justify-between"
                            >
                                <span className="font-mono-tech text-[10px] text-[#52525b]">
                                    {k}
                                </span>
                                <span className="font-mono-tech text-[11px]">
                                    {v}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                <form
                    onSubmit={onSubmit}
                    data-testid="booking-form"
                    className="col-span-12 lg:col-span-8 p-6 md:p-12"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                            <Label idx="01" htmlFor="full_name">
                                Full name
                            </Label>
                            <input
                                id="full_name"
                                data-testid="input-full-name"
                                className={inputCls}
                                value={form.full_name}
                                onChange={(e) => set("full_name", e.target.value)}
                                placeholder="Alex Driver"
                            />
                        </div>
                        <div>
                            <Label idx="02" htmlFor="phone">
                                Phone
                            </Label>
                            <input
                                id="phone"
                                data-testid="input-phone"
                                className={inputCls}
                                value={form.phone}
                                onChange={(e) => set("phone", e.target.value)}
                                placeholder="+1 555 555 5555"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <Label idx="03" htmlFor="email">
                                Email
                            </Label>
                            <input
                                id="email"
                                type="email"
                                data-testid="input-email"
                                className={inputCls}
                                value={form.email}
                                onChange={(e) => set("email", e.target.value)}
                                placeholder="you@email.com"
                            />
                        </div>
                        <div>
                            <Label idx="04" htmlFor="vehicle_year">
                                Vehicle year
                            </Label>
                            <input
                                id="vehicle_year"
                                data-testid="input-year"
                                className={inputCls}
                                value={form.vehicle_year}
                                onChange={(e) => set("vehicle_year", e.target.value)}
                                placeholder="2020"
                            />
                        </div>
                        <div>
                            <Label idx="05" htmlFor="vehicle_make">
                                Make
                            </Label>
                            <input
                                id="vehicle_make"
                                data-testid="input-make"
                                className={inputCls}
                                value={form.vehicle_make}
                                onChange={(e) => set("vehicle_make", e.target.value)}
                                placeholder="Toyota"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <Label idx="06" htmlFor="vehicle_model">
                                Model
                            </Label>
                            <input
                                id="vehicle_model"
                                data-testid="input-model"
                                className={inputCls}
                                value={form.vehicle_model}
                                onChange={(e) => set("vehicle_model", e.target.value)}
                                placeholder="Camry"
                            />
                        </div>

                        <div>
                            <Label idx="07">Service needed</Label>
                            <Select
                                value={form.service_type}
                                onValueChange={(v) => set("service_type", v)}
                            >
                                <SelectTrigger
                                    data-testid="select-service"
                                    className={`${inputCls} h-auto justify-between`}
                                >
                                    <SelectValue placeholder="Choose a service" />
                                </SelectTrigger>
                                <SelectContent className="border border-[#0a0a0a] rounded-none">
                                    {SERVICE_OPTIONS.map((s) => (
                                        <SelectItem
                                            key={s}
                                            value={s}
                                            data-testid={`service-option-${s}`}
                                            className="font-medium rounded-none"
                                        >
                                            {s}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label idx="08">Preferred time</Label>
                            <Select
                                value={form.preferred_time}
                                onValueChange={(v) => set("preferred_time", v)}
                            >
                                <SelectTrigger
                                    data-testid="select-time"
                                    className={`${inputCls} h-auto justify-between`}
                                >
                                    <SelectValue placeholder="Choose a time window" />
                                </SelectTrigger>
                                <SelectContent className="border border-[#0a0a0a] rounded-none">
                                    {TIME_OPTIONS.map((t) => (
                                        <SelectItem
                                            key={t}
                                            value={t}
                                            data-testid={`time-option-${t}`}
                                            className="font-mono-tech text-xs rounded-none"
                                        >
                                            {t}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="md:col-span-2">
                            <Label idx="09">Preferred date</Label>
                            <Popover open={dateOpen} onOpenChange={setDateOpen}>
                                <PopoverTrigger asChild>
                                    <button
                                        type="button"
                                        data-testid="button-date"
                                        className={`${inputCls} text-left`}
                                    >
                                        {date
                                            ? format(date, "EEEE, MMMM d, yyyy")
                                            : "Pick a date"}
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent
                                    align="start"
                                    className="p-0 border border-[#0a0a0a] rounded-none bg-white"
                                >
                                    <Calendar
                                        mode="single"
                                        selected={date}
                                        onSelect={(d) => {
                                            setDate(d);
                                            if (d) {
                                                set(
                                                    "preferred_date",
                                                    format(d, "yyyy-MM-dd")
                                                );
                                            }
                                            setDateOpen(false);
                                        }}
                                        disabled={(d) => {
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);
                                            return d < today;
                                        }}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>

                        <div className="md:col-span-2">
                            <Label idx="10" htmlFor="address">
                                Service address
                            </Label>
                            <input
                                id="address"
                                data-testid="input-address"
                                className={inputCls}
                                value={form.address}
                                onChange={(e) => set("address", e.target.value)}
                                placeholder="123 Main St, City, State"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <Label idx="11" htmlFor="issue">
                                Describe the issue (optional)
                            </Label>
                            <textarea
                                id="issue"
                                data-testid="input-issue"
                                rows={4}
                                className={inputCls + " resize-none"}
                                value={form.issue}
                                onChange={(e) => set("issue", e.target.value)}
                                placeholder="Strange noise when braking, check engine light on, ..."
                            />
                        </div>
                    </div>

                    <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t hairline pt-6">
                        <div className="font-mono-tech text-[10px] text-[#52525b] max-w-md">
                            By submitting you agree to be contacted by phone or
                            SMS regarding your booking. No spam.
                        </div>
                        <button
                            type="submit"
                            data-testid="submit-booking"
                            disabled={submitting}
                            className="btn-primary px-7 py-4 font-mono-tech text-[12px] inline-flex items-center gap-2 disabled:opacity-60"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Sending…
                                </>
                            ) : (
                                <>
                                    Submit booking
                                    <ArrowRight className="w-4 h-4" />
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </section>
    );
}
